import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { MessageCircle, Send, Users, ShoppingBag, Wrench, Search, Shield, Filter, Plus, X, ArrowLeft, Phone, MoreVertical, Image, Smile, Check, CheckCheck, Zap, Volume2, VolumeX, Circle, Bot, Ticket, CheckCircle, Bolt, Pencil, Trash2 } from 'lucide-react';

const MSN_STATUSES = [
  { key: 'online', label: 'En ligne', color: '#4ade80', icon: Circle },
  { key: 'busy', label: 'Occupe', color: '#ef4444', icon: Circle },
  { key: 'away', label: 'Absent', color: '#f59e0b', icon: Circle },
  { key: 'offline', label: 'Hors ligne', color: '#6b7280', icon: Circle },
];

const QUICK_EMOJIS = ['👋', '😊', '😂', '👍', '❤️', '🔥', '💯', '🎉', '😎', '🤝', '📱', '🔧'];

const QR_COLORS = {
  yellow: 'bg-yellow-500/15 text-yellow-400 border-yellow-500/25 hover:bg-yellow-500/25',
  blue: 'bg-blue-500/15 text-blue-400 border-blue-500/25 hover:bg-blue-500/25',
  green: 'bg-green-500/15 text-green-400 border-green-500/25 hover:bg-green-500/25',
  orange: 'bg-orange-500/15 text-orange-400 border-orange-500/25 hover:bg-orange-500/25',
  purple: 'bg-purple-500/15 text-purple-400 border-purple-500/25 hover:bg-purple-500/25',
  cyan: 'bg-cyan-500/15 text-cyan-400 border-cyan-500/25 hover:bg-cyan-500/25',
  red: 'bg-red-500/15 text-red-400 border-red-500/25 hover:bg-red-500/25',
};

const QR_COLOR_OPTIONS = [
  { key: 'yellow', label: 'Jaune', class: 'bg-yellow-500' },
  { key: 'blue', label: 'Bleu', class: 'bg-blue-500' },
  { key: 'green', label: 'Vert', class: 'bg-green-500' },
  { key: 'orange', label: 'Orange', class: 'bg-orange-500' },
  { key: 'purple', label: 'Violet', class: 'bg-purple-500' },
  { key: 'cyan', label: 'Cyan', class: 'bg-cyan-500' },
  { key: 'red', label: 'Rouge', class: 'bg-red-500' },
];

const QuickRepliesAdmin = ({ api, quickReplies, setQuickReplies, fetchQuickReplies }) => {
  const { t } = useLanguage();
  const [editingQR, setEditingQR] = useState(null);
  const [qrForm, setQrForm] = useState({ label: '', text: '', color: 'blue' });
  const [saving, setSaving] = useState(false);

  const addReply = async () => {
    if (!qrForm.label || !qrForm.text) return;
    setSaving(true);
    try {
      await api.post('/quick-replies', qrForm);
      setQrForm({ label: '', text: '', color: 'blue' });
      await fetchQuickReplies();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  const deleteReply = async (id) => {
    try {
      await api.delete(`/quick-replies/${id}`);
      await fetchQuickReplies();
    } catch (e) { console.error(e); }
  };

  const startEdit = (qr) => {
    setEditingQR(qr.id);
    setQrForm({ label: qr.label, text: qr.text, color: qr.color || 'blue' });
  };

  const saveEdit = async () => {
    if (!qrForm.label || !qrForm.text) return;
    setSaving(true);
    try {
      const updated = quickReplies.map(qr => qr.id === editingQR ? { ...qr, ...qrForm } : qr);
      await api.put('/quick-replies', updated);
      setEditingQR(null);
      setQrForm({ label: '', text: '', color: 'blue' });
      await fetchQuickReplies();
    } catch (e) { console.error(e); }
    setSaving(false);
  };

  return (
    <div className="p-3 space-y-3" data-testid="quick-replies-admin">
      <div className="text-center mb-2">
        <Bolt className="w-5 h-5 text-cyan-400 mx-auto mb-1" />
        <p className="text-xs font-bold text-white">{t('staffChat.quickRepliesTitle')}</p>
        <p className="text-[10px] text-gray-600">{t('staffChat.quickRepliesSubtitle')}</p>
      </div>

      {/* Add/Edit form */}
      <div className="p-3 rounded-xl bg-white/[0.03] border border-white/5 space-y-2">
        <input value={qrForm.label} onChange={e => setQrForm({...qrForm, label: e.target.value})}
          placeholder={t('staffChat.qrLabelPlaceholder')} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white placeholder-gray-600" data-testid="qr-form-label" />
        <textarea value={qrForm.text} onChange={e => setQrForm({...qrForm, text: e.target.value})} rows={2}
          placeholder={t('staffChat.qrTextPlaceholder')} className="w-full px-3 py-2 rounded-lg bg-white/5 border border-white/10 text-xs text-white placeholder-gray-600" data-testid="qr-form-text" />
        <div className="flex items-center gap-1.5">
          {QR_COLOR_OPTIONS.map(c => (
            <button key={c.key} onClick={() => setQrForm({...qrForm, color: c.key})}
              className={`w-5 h-5 rounded-full ${c.class} transition-all ${qrForm.color === c.key ? 'ring-2 ring-white scale-110' : 'opacity-50 hover:opacity-80'}`}
              title={t('staffChat.color' + c.key.charAt(0).toUpperCase() + c.key.slice(1))} data-testid={`qr-color-${c.key}`} />
          ))}
        </div>
        <div className="flex gap-2">
          {editingQR ? (
            <>
              <button onClick={saveEdit} disabled={saving} className="flex-1 px-3 py-1.5 rounded-lg bg-green-600 text-white text-[10px] font-bold hover:bg-green-500 transition-colors disabled:opacity-50" data-testid="qr-save-edit">
                {t('staffChat.qrSaveEdit')}
              </button>
              <button onClick={() => { setEditingQR(null); setQrForm({ label: '', text: '', color: 'blue' }); }} className="px-3 py-1.5 rounded-lg bg-white/5 text-gray-400 text-[10px] font-bold hover:bg-white/10 transition-colors">
                {t('staffChat.cancel')}
              </button>
            </>
          ) : (
            <button onClick={addReply} disabled={saving || !qrForm.label || !qrForm.text} className="flex-1 px-3 py-1.5 rounded-lg bg-blue-600 text-white text-[10px] font-bold hover:bg-blue-500 transition-colors disabled:opacity-50" data-testid="qr-add-btn">
              + {t('staffChat.add')}
            </button>
          )}
        </div>
      </div>

      {/* List */}
      <div className="space-y-1.5">
        {quickReplies.map((qr, i) => (
          <div key={qr.id || i} className="p-2.5 rounded-xl bg-white/[0.02] border border-white/5 hover:bg-white/[0.04] transition-all group" data-testid={`qr-item-${i}`}>
            <div className="flex items-center justify-between mb-1">
              <div className="flex items-center gap-2">
                <span className={`w-2.5 h-2.5 rounded-full ${QR_COLOR_OPTIONS.find(c => c.key === qr.color)?.class || 'bg-blue-500'}`} />
                <span className="text-xs font-bold text-white">{qr.label}</span>
              </div>
              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                <button onClick={() => startEdit(qr)} className="p-1 rounded hover:bg-white/10 text-gray-500 hover:text-blue-400 transition-colors" data-testid={`qr-edit-${i}`}>
                  <Pencil className="w-3 h-3" />
                </button>
                <button onClick={() => deleteReply(qr.id)} className="p-1 rounded hover:bg-white/10 text-gray-500 hover:text-red-400 transition-colors" data-testid={`qr-delete-${i}`}>
                  <Trash2 className="w-3 h-3" />
                </button>
              </div>
            </div>
            <p className="text-[10px] text-gray-500 line-clamp-2">{qr.text}</p>
          </div>
        ))}
      </div>
    </div>
  );
};

const ChatPage = () => {
  const { api, user } = useAuth();
  const { t, formatDate } = useLanguage();
  const [conversations, setConversations] = useState([]);
  const [selectedConvo, setSelectedConvo] = useState(null);
  const [messages, setMessages] = useState([]);
  const [message, setMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [showNewDM, setShowNewDM] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [mobileView, setMobileView] = useState('list');
  const [myStatus, setMyStatus] = useState('online');
  const [showStatusMenu, setShowStatusMenu] = useState(false);
  const [showEmojis, setShowEmojis] = useState(false);
  const [showQuickReplies, setShowQuickReplies] = useState(false);
  const [nudging, setNudging] = useState(false);
  const [supportTickets, setSupportTickets] = useState([]);
  const [ticketsLoading, setTicketsLoading] = useState(false);
  const [quickReplies, setQuickReplies] = useState([]);
  const pollRef = useRef(null);
  const endRef = useRef(null);
  const userScrolledUpRef = useRef(false);
  const messagesContainerRef = useRef(null);

  useEffect(() => { fetchConversations(); fetchSupportTickets(); fetchQuickReplies(); }, []);

  useEffect(() => {
    if (endRef.current && !userScrolledUpRef.current) {
      endRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  const handleMessagesScroll = (e) => {
    const el = e.target;
    const isNearBottom = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    userScrolledUpRef.current = !isNearBottom;
  };

  useEffect(() => {
    if (!selectedConvo) return;
    userScrolledUpRef.current = false;
    const load = async () => {
      try {
        const res = await api.get(`/chat/conversations/${selectedConvo.id}/messages`);
        setMessages(res.data || []);
        await api.post(`/chat/conversations/${selectedConvo.id}/mark-read`).catch(() => {});
      } catch (err) { console.error(err); }
    };
    load();
    pollRef.current = setInterval(async () => {
      try {
        const res = await api.get(`/chat/conversations/${selectedConvo.id}/messages`);
        setMessages(res.data || []);
      } catch {}
    }, 3000);
    return () => { if (pollRef.current) clearInterval(pollRef.current); };
  }, [selectedConvo, api]);

  const fetchConversations = async () => {
    try {
      const res = await api.get('/chat/conversations');
      const allConvos = (res.data || []).map(c => ({
        ...c,
        _type: c.related_type === 'staff_dm' ? 'staff' : 'client'
      }));
      setConversations(allConvos);
      if (allConvos.length > 0 && !selectedConvo) setSelectedConvo(allConvos[0]);
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const openNewDM = async () => {
    try {
      const res = await api.get('/chat/staff-list');
      setStaffList(res.data || []);
      setShowNewDM(true);
    } catch (err) { console.error(err); }
  };

  const startDM = async (targetId) => {
    try {
      const res = await api.post('/chat/staff-dm', { target_id: targetId });
      setShowNewDM(false);
      await fetchConversations();
      setSelectedConvo(res.data);
      setMobileView('chat');
    } catch (err) { console.error(err); }
  };

  const selectConvo = (convo) => { setSelectedConvo(convo); setMobileView('chat'); };
  const backToList = () => { setMobileView('list'); };

  const sendMessage = async () => {
    if (!message.trim() || !selectedConvo) return;
    const content = message.trim();
    setMessage('');
    setSending(true);
    userScrolledUpRef.current = false;
    try {
      await api.post(`/chat/conversations/${selectedConvo.id}/messages`, { content });
      const res = await api.get(`/chat/conversations/${selectedConvo.id}/messages`);
      setMessages(res.data || []);
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const sendNudge = async () => {
    if (!selectedConvo || nudging) return;
    setNudging(true);
    try {
      await api.post(`/chat/conversations/${selectedConvo.id}/messages`, { content: '🔔 *Nudge!* 🔔' });
      const res = await api.get(`/chat/conversations/${selectedConvo.id}/messages`);
      setMessages(res.data || []);
    } catch {}
    setTimeout(() => setNudging(false), 1500);
  };

  const fetchSupportTickets = async () => {
    setTicketsLoading(true);
    try {
      const res = await api.get('/chat/support-tickets');
      setSupportTickets(res.data || []);
    } catch {}
    setTicketsLoading(false);
  };

  const fetchQuickReplies = async () => {
    try {
      const res = await api.get('/quick-replies');
      setQuickReplies(res.data || []);
    } catch {}
  };

  const closeTicket = async (ticketId) => {
    try {
      await api.post(`/chat/support-tickets/${ticketId}/close`);
      setSupportTickets(prev => prev.map(t => t.id === ticketId ? { ...t, status: 'closed' } : t));
    } catch (err) { console.error(err); }
  };

  const openTickets = supportTickets.filter(t => t.status === 'open');
  const getTicketPriority = (ticket) => {
    const hours = (Date.now() - new Date(ticket.created_at).getTime()) / 3600000;
    if (hours > 24) return { label: t('staffChat.priorityUrgent'), color: 'bg-red-500/20 text-red-400 border-red-500/30' };
    if (hours > 6) return { label: t('staffChat.priorityMedium'), color: 'bg-amber-500/20 text-amber-400 border-amber-500/30' };
    return { label: t('staffChat.priorityNormal'), color: 'bg-blue-500/20 text-blue-400 border-blue-500/30' };
  };

  const goToTicketConvo = (ticket) => {
    const convo = conversations.find(c => c.id === ticket.conversation_id);
    if (convo) {
      setFilter('all');
      selectConvo(convo);
    }
  };

  const sendQuickReply = async (text) => {
    if (!selectedConvo || sending) return;
    setSending(true);
    setShowQuickReplies(false);
    userScrolledUpRef.current = false;
    try {
      await api.post(`/chat/conversations/${selectedConvo.id}/messages`, { content: text });
      const res = await api.get(`/chat/conversations/${selectedConvo.id}/messages`);
      setMessages(res.data || []);
    } catch (err) { console.error(err); }
    finally { setSending(false); }
  };

  const getIcon = (convo) => {
    if (convo._type === 'staff') return <Shield className="w-4 h-4" />;
    if (convo.related_type === 'boutique_order') return <ShoppingBag className="w-4 h-4" />;
    if (convo.related_type === 'repair') return <Wrench className="w-4 h-4" />;
    return <Users className="w-4 h-4" />;
  };

  const getLabel = (convo) => {
    if (convo._type === 'staff') return t('staffChat.labelTeam');
    if (convo.related_type === 'boutique_order') return t('staffChat.labelOrder');
    if (convo.related_type === 'repair') return t('staffChat.labelRepair');
    return t('staffChat.labelClient');
  };

  const getAvatarColor = (convo) => {
    if (convo._type === 'staff') return 'from-blue-500 to-cyan-500';
    if (convo.related_type === 'boutique_order') return 'from-amber-500 to-orange-500';
    if (convo.related_type === 'repair') return 'from-orange-500 to-red-500';
    return 'from-emerald-500 to-teal-500';
  };

  const getInitials = (convo) => {
    const name = convo.client_nom || convo.display_title || convo.title || '?';
    return name.split(' ').map(w => w[0]).join('').toUpperCase().slice(0, 2);
  };

  const filtered = conversations.filter(c => {
    if (filter === 'staff' && c._type !== 'staff') return false;
    if (filter === 'clients' && c._type !== 'client') return false;
    if (searchTerm) {
      const s = searchTerm.toLowerCase();
      return (c.title || '').toLowerCase().includes(s) ||
             (c.client_nom || '').toLowerCase().includes(s) ||
             (c.display_title || '').toLowerCase().includes(s);
    }
    return true;
  });

  const unreadTotal = conversations.reduce((s, c) => s + (c.unread_count || 0), 0);
  const unreadStaff = conversations.filter(c => c._type === 'staff').reduce((s, c) => s + (c.unread_count || 0), 0);
  const unreadClients = conversations.filter(c => c._type === 'client').reduce((s, c) => s + (c.unread_count || 0), 0);

  // Group messages by date
  const groupMessagesByDate = (msgs) => {
    const groups = [];
    let currentDate = null;
    msgs.forEach(msg => {
      const d = msg.created_at ? formatDate(new Date(msg.created_at), { weekday: 'long', day: 'numeric', month: 'long' }) : '';
      if (d !== currentDate) {
        currentDate = d;
        groups.push({ type: 'date', date: d });
      }
      groups.push({ type: 'message', data: msg });
    });
    return groups;
  };

  return (
    <Layout>
      <div className="h-[calc(100vh-200px)] lg:h-[calc(100vh-120px)] flex flex-col" data-testid="chat-page">
        {/* Main container - MSN Messenger inspired */}
        <div className={`flex-1 flex overflow-hidden rounded-2xl border shadow-2xl ${nudging ? 'animate-shake' : ''}`} style={{background: 'linear-gradient(145deg, rgba(12,12,20,0.98), rgba(8,8,14,0.99))', borderColor: 'rgba(59,130,246,0.2)'}}>
          
          {/* LEFT SIDEBAR - MSN Contact List */}
          <div className={`${mobileView === 'chat' ? 'hidden lg:flex' : 'flex'} w-full lg:w-[360px] xl:w-[400px] flex-col border-r shrink-0`} style={{borderColor: 'rgba(59,130,246,0.15)'}}>
            
            {/* MSN-style Header with gradient */}
            <div className="p-4 border-b" style={{background: 'linear-gradient(135deg, #1e3a5f 0%, #0c4a6e 50%, #164e63 100%)', borderColor: 'rgba(59,130,246,0.2)'}}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-3">
                  <div className="w-11 h-11 rounded-full bg-gradient-to-br from-blue-400 to-cyan-400 flex items-center justify-center text-white font-bold shadow-lg shadow-blue-500/30">
                    {user?.username?.[0]?.toUpperCase() || 'U'}
                  </div>
                  <div>
                    <h1 className="text-base font-bold text-white" data-testid="chat-title">{user?.username || t('staffChat.messaging')}</h1>
                    <div className="relative">
                      <button onClick={() => setShowStatusMenu(!showStatusMenu)} className="flex items-center gap-1.5 text-[11px] text-blue-200/80 hover:text-white transition-colors" data-testid="status-selector">
                        <Circle className="w-2 h-2 fill-current" style={{ color: MSN_STATUSES.find(s => s.key === myStatus)?.color }} />
                        {t('staffChat.status' + myStatus.charAt(0).toUpperCase() + myStatus.slice(1))}
                      </button>
                      {showStatusMenu && (
                        <>
                          <div className="fixed inset-0 z-40" onClick={() => setShowStatusMenu(false)} />
                          <div className="absolute left-0 top-full mt-1 w-36 rounded-lg border border-blue-500/20 bg-gray-900/95 backdrop-blur-xl shadow-xl z-50 overflow-hidden">
                            {MSN_STATUSES.map(s => (
                              <button key={s.key} onClick={() => { setMyStatus(s.key); setShowStatusMenu(false); }}
                                className={`w-full flex items-center gap-2 px-3 py-2 text-xs hover:bg-white/10 transition-colors ${myStatus === s.key ? 'text-white bg-white/5' : 'text-gray-400'}`}>
                                <Circle className="w-2.5 h-2.5 fill-current" style={{ color: s.color }} />
                                {t('staffChat.status' + s.key.charAt(0).toUpperCase() + s.key.slice(1))}
                              </button>
                            ))}
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  {unreadTotal > 0 && (
                    <span className="px-2.5 py-1 rounded-full bg-red-500 text-white text-[11px] font-bold shadow-lg shadow-red-500/30 animate-pulse">
                      {unreadTotal}
                    </span>
                  )}
                  <button onClick={openNewDM} className="w-9 h-9 rounded-xl bg-white/10 hover:bg-white/20 text-blue-200 hover:text-white flex items-center justify-center transition-all" title={t('staffChat.newConversation')} data-testid="new-conversation-btn">
                    <Plus className="w-4 h-4" />
                  </button>
                </div>
              </div>

              {/* Filter pills */}
              <div className="flex gap-1.5 mb-3">
                {[
                  { key: 'all', label: t('staffChat.filterAll'), count: unreadTotal },
                  { key: 'staff', label: t('staffChat.filterTeam'), count: unreadStaff, icon: Shield },
                  { key: 'clients', label: t('staffChat.filterClients'), count: unreadClients, icon: Users },
                  { key: 'tickets', label: t('staffChat.filterTickets'), count: openTickets.length, icon: Ticket },
                  ...(user?.role === 'admin' ? [{ key: 'rapides', label: t('staffChat.filterQuick'), count: 0, icon: Bolt }] : []),
                ].map(f => (
                  <button key={f.key} onClick={() => setFilter(f.key)}
                    className={`flex-1 flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all ${
                      filter === f.key
                        ? f.key === 'tickets' ? 'bg-amber-500/20 text-amber-300 shadow-inner' : 'bg-white/20 text-white shadow-inner'
                        : 'text-blue-200/60 hover:text-white hover:bg-white/10'
                    }`}
                    data-testid={`filter-${f.key}`}>
                    {f.icon && <f.icon className="w-3.5 h-3.5" />}
                    {f.label}
                    {f.count > 0 && <span className={`ml-1 w-4 h-4 rounded-full text-white text-[9px] flex items-center justify-center ${f.key === 'tickets' ? 'bg-amber-500' : 'bg-red-500'}`}>{f.count}</span>}
                  </button>
                ))}
              </div>

              {/* Search */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-blue-300/50" />
                <input type="text" placeholder={t('staffChat.searchPlaceholder')}
                  value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2.5 rounded-xl text-sm bg-white/10 border border-white/10 text-white placeholder-blue-200/40 focus:border-blue-400/40 focus:bg-white/15 outline-none transition-all"
                  data-testid="chat-search" />
              </div>
            </div>

            {/* New DM panel */}
            {showNewDM && (
              <div className="border-b p-4 space-y-3" style={{background: 'rgba(14,36,58,0.8)', borderColor: 'rgba(59,130,246,0.15)'}}>
                <div className="flex items-center justify-between">
                  <span className="text-xs font-bold text-blue-200">{t('staffChat.newConversation')}</span>
                  <button onClick={() => setShowNewDM(false)} className="text-blue-300/50 hover:text-white transition-colors"><X className="w-4 h-4" /></button>
                </div>
                {staffList.length === 0 ? (
                  <p className="text-xs text-gray-600">{t('staffChat.noStaffAvailable')}</p>
                ) : (
                  <div className="space-y-1">
                    {staffList.map(s => (
                      <button key={s.id} onClick={() => startDM(s.id)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-white/[0.06] text-left transition-all" data-testid={`dm-staff-${s.id}`}>
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center text-white text-xs font-bold shadow-md">
                          {(s.username || s.nom || '?')[0].toUpperCase()}
                        </div>
                        <div>
                          <span className="text-sm text-white font-medium">{s.username || s.nom}</span>
                          <span className="ml-2 text-[10px] text-gray-500 capitalize">{s.role}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Conversations list OR Tickets panel */}
            <div className="flex-1 overflow-y-auto" style={{WebkitOverflowScrolling: 'touch'}}>
              {filter === 'rapides' ? (
                /* ── QUICK REPLIES ADMIN ── */
                <QuickRepliesAdmin api={api} quickReplies={quickReplies} setQuickReplies={setQuickReplies} fetchQuickReplies={fetchQuickReplies} />
              ) : filter === 'tickets' ? (
                /* ── TICKETS PANEL ── */
                <div className="p-3 space-y-2" data-testid="tickets-panel">
                  {ticketsLoading ? (
                    <div className="p-8 text-center"><div className="loading-spinner mx-auto"></div></div>
                  ) : openTickets.length === 0 ? (
                    <div className="p-8 text-center">
                      <div className="w-14 h-14 rounded-full bg-green-500/10 flex items-center justify-center mx-auto mb-3">
                        <CheckCircle className="w-6 h-6 text-green-400" />
                      </div>
                      <p className="text-sm text-green-400 font-medium">{t('staffChat.ticketsNone')}</p>
                      <p className="text-xs text-gray-600 mt-1">{t('staffChat.ticketsAllHandled')}</p>
                    </div>
                  ) : (
                    openTickets.map(ticket => {
                      const priority = getTicketPriority(ticket);
                      const hoursAgo = Math.round((Date.now() - new Date(ticket.created_at).getTime()) / 3600000);
                      return (
                        <div key={ticket.id} className="p-3 rounded-xl border border-amber-500/15 bg-amber-500/5 hover:bg-amber-500/10 transition-all" data-testid={`ticket-${ticket.id}`}>
                          <div className="flex items-start justify-between gap-2 mb-2">
                            <div className="flex items-center gap-2 min-w-0">
                              <Bot className="w-4 h-4 text-amber-400 shrink-0" />
                              <span className="text-sm font-semibold text-white truncate">{ticket.client_name || t('staffChat.labelClient')}</span>
                            </div>
                            <span className={`text-[9px] px-1.5 py-0.5 rounded border shrink-0 font-bold ${priority.color}`}>{priority.label}</span>
                          </div>
                          <p className="text-xs text-gray-300 mb-2 line-clamp-2">{ticket.message_preview}</p>
                          <div className="flex items-center justify-between">
                            <span className="text-[10px] text-gray-600">{hoursAgo < 1 ? t('staffChat.timeAgoLessThanHour') : t('staffChat.timeAgoHours', { h: hoursAgo })}</span>
                            <div className="flex items-center gap-1.5">
                              <button onClick={() => goToTicketConvo(ticket)} className="px-2.5 py-1 rounded-lg bg-blue-500/20 text-blue-400 text-[10px] font-semibold hover:bg-blue-500/30 transition-colors" data-testid={`ticket-view-${ticket.id}`}>
                                {t('staffChat.viewConversation')}
                              </button>
                              <button onClick={() => closeTicket(ticket.id)} className="px-2.5 py-1 rounded-lg bg-green-500/20 text-green-400 text-[10px] font-semibold hover:bg-green-500/30 transition-colors" data-testid={`ticket-close-${ticket.id}`}>
                                {t('staffChat.close')}
                              </button>
                            </div>
                          </div>
                        </div>
                      );
                    })
                  )}
                  {/* Closed tickets */}
                  {supportTickets.filter(t => t.status === 'closed').length > 0 && (
                    <div className="mt-4 pt-3 border-t border-white/5">
                      <p className="text-[10px] text-gray-600 uppercase tracking-wider mb-2 px-1">{t('staffChat.recentlyClosed')}</p>
                      {supportTickets.filter(t => t.status === 'closed').slice(0, 5).map(ticket => (
                        <div key={ticket.id} className="px-3 py-2 rounded-lg bg-white/[0.02] flex items-center justify-between gap-2 mb-1" data-testid={`ticket-closed-${ticket.id}`}>
                          <div className="flex items-center gap-2 min-w-0">
                            <CheckCircle className="w-3 h-3 text-green-500/50 shrink-0" />
                            <span className="text-[11px] text-gray-500 truncate">{ticket.client_name}</span>
                          </div>
                          <span className="text-[9px] text-gray-700 shrink-0">{t('staffChat.closedBy', { name: ticket.closed_by || '?' })}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ) : loading ? (
                <div className="p-8 text-center"><div className="loading-spinner mx-auto"></div></div>
              ) : filtered.length === 0 ? (
                <div className="p-10 text-center text-gray-600">
                  <div className="w-16 h-16 rounded-full bg-white/[0.03] flex items-center justify-center mx-auto mb-4">
                    <Filter className="w-7 h-7 opacity-30" />
                  </div>
                  <p className="text-sm">{t('staffChat.noConversations')}</p>
                </div>
              ) : (
                filtered.map(convo => (
                  <div
                    key={convo.id}
                    onClick={() => selectConvo(convo)}
                    className={`px-4 py-3.5 cursor-pointer transition-all border-l-[3px] ${
                      selectedConvo?.id === convo.id
                        ? 'bg-white/[0.06] border-l-transparent'
                        : 'hover:bg-white/[0.03] border-l-transparent'
                    }`}
                    style={selectedConvo?.id === convo.id ? { borderLeftColor: 'var(--theme-primary)' } : {}}
                    data-testid={`convo-${convo.id}`}
                  >
                    <div className="flex items-center gap-3">
                      <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${getAvatarColor(convo)} flex items-center justify-center text-white text-sm font-bold shadow-md shrink-0`}>
                        {getInitials(convo)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-[13px] font-semibold text-white truncate">
                            {convo.client_nom || convo.display_title || convo.title || t('staffChat.conversation')}
                          </span>
                          <span className="text-[10px] text-gray-600 shrink-0">
                            {(convo.updated_at || convo.created_at) ? formatDate(new Date(convo.updated_at || convo.created_at), { day: '2-digit', month: 'short' }) : ''}
                          </span>
                        </div>
                        <div className="flex items-center justify-between mt-1">
                          <div className="flex items-center gap-2 min-w-0">
                            <span className={`text-[10px] font-semibold px-1.5 py-0.5 rounded-md ${
                              convo._type === 'staff' ? 'bg-blue-500/10 text-blue-400' : 'bg-emerald-500/10 text-emerald-400'
                            }`}>{getLabel(convo)}</span>
                            {convo.last_message && (
                              <p className="text-[11px] text-gray-600 truncate">
                                {typeof convo.last_message === 'object' ? convo.last_message.content : convo.last_message}
                              </p>
                            )}
                          </div>
                          {convo.unread_count > 0 && (
                            <span className="shrink-0 w-5 h-5 rounded-full text-white text-[10px] font-bold flex items-center justify-center shadow-lg" style={{ background: 'var(--theme-primary)', boxShadow: '0 2px 8px rgba(var(--theme-primary-rgb), 0.4)' }}>
                              {convo.unread_count}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* RIGHT: MESSAGES - MSN Chat Window */}
          <div className={`${mobileView === 'list' ? 'hidden lg:flex' : 'flex'} flex-1 flex-col min-w-0`}>
            {selectedConvo ? (
              <>
                {/* MSN-style Chat header */}
                <div className="px-5 py-3 border-b flex items-center justify-between shrink-0" style={{background: 'linear-gradient(135deg, #1e3a5f, #0c4a6e)', borderColor: 'rgba(59,130,246,0.2)'}}>
                  <div className="flex items-center gap-3">
                    <button onClick={backToList} className="lg:hidden w-9 h-9 rounded-xl bg-white/10 flex items-center justify-center text-blue-200 hover:text-white transition-colors shrink-0" data-testid="chat-back-btn-header">
                      <ArrowLeft className="w-4 h-4" />
                    </button>
                    <div className="relative">
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${getAvatarColor(selectedConvo)} flex items-center justify-center text-white text-sm font-bold shadow-md shrink-0`}>
                        {getInitials(selectedConvo)}
                      </div>
                      <div className="absolute -bottom-0.5 -right-0.5 w-3.5 h-3.5 rounded-full bg-emerald-500 border-2 border-gray-900 shadow-sm"></div>
                    </div>
                    <div className="min-w-0">
                      <h3 className="font-bold text-white text-sm truncate">
                        {selectedConvo.client_nom || selectedConvo.display_title || selectedConvo.title}
                      </h3>
                      <div className="flex items-center gap-1.5">
                        <Circle className="w-2 h-2 fill-emerald-500 text-emerald-500" />
                        <p className="text-[11px] text-blue-200/70">{getLabel(selectedConvo)} - {t('staffChat.statusOnline')}</p>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button onClick={sendNudge} disabled={nudging}
                      className="w-8 h-8 rounded-lg bg-white/10 hover:bg-amber-500/20 text-blue-200 hover:text-amber-400 flex items-center justify-center transition-all disabled:opacity-50"
                      title={t('staffChat.nudgeTitle')} data-testid="nudge-btn">
                      <Zap className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Messages area */}
                <div
                  ref={messagesContainerRef}
                  onScroll={handleMessagesScroll}
                  className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 min-h-0"
                  style={{
                    background: 'linear-gradient(180deg, rgba(10,10,16,0.8) 0%, rgba(14,14,22,0.6) 100%)',
                    WebkitOverflowScrolling: 'touch'
                  }}
                >
                  {messages.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-gray-600">
                      <div className="text-center">
                        <div className="w-20 h-20 rounded-full bg-white/[0.03] flex items-center justify-center mx-auto mb-4">
                          <MessageCircle className="w-9 h-9 opacity-20" />
                        </div>
                        <p className="text-sm text-gray-500">{t('staffChat.startConversation')}</p>
                        <p className="text-xs text-gray-700 mt-1">{t('staffChat.endToEndEncrypted')}</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-1">
                      {groupMessagesByDate(messages).map((item, idx) => {
                        if (item.type === 'date') {
                          return (
                            <div key={`date-${idx}`} className="flex justify-center py-3">
                              <span className="text-[11px] text-gray-500 bg-white/[0.04] px-4 py-1.5 rounded-full backdrop-blur-sm">{item.date}</span>
                            </div>
                          );
                        }
                        const msg = item.data;
                        const isMe = msg.sender_id === user?.id;
                        const isAi = msg.sender_role === 'ai' || msg.is_ai_reply;
                        const prevItem = idx > 0 ? groupMessagesByDate(messages)[idx - 1] : null;
                        const isSameSender = prevItem?.type === 'message' && prevItem.data.sender_id === msg.sender_id;
                        return (
                          <div key={idx} className={`flex ${isMe ? 'justify-end' : 'justify-start'} ${isSameSender ? 'mt-0.5' : 'mt-3'}`}
                            style={{animation: 'fadeSlideIn 0.25s ease-out'}}>
                            {isAi && !isSameSender && (
                              <div className="w-7 h-7 rounded-full bg-amber-500/20 flex items-center justify-center shrink-0 mr-2 mt-5">
                                <Bot className="w-4 h-4 text-amber-400" />
                              </div>
                            )}
                            {isAi && isSameSender && <div className="w-7 mr-2 shrink-0" />}
                            <div className={`max-w-[75%] sm:max-w-[65%] group`}>
                              {!isMe && !isSameSender && (
                                <span className={`text-[10px] font-medium ml-3 mb-0.5 block ${isAi ? 'text-amber-400' : 'text-gray-500'}`}>
                                  {isAi ? t('staffChat.aiAssistant') : (msg.sender_name || t('staffChat.labelClient'))}
                                  {isAi && <span className="ml-1.5 text-[8px] bg-amber-500/20 px-1.5 py-0.5 rounded-full">{t('staffChat.autoBadge')}</span>}
                                </span>
                              )}
                              <div className={`px-3.5 py-2.5 text-[13.5px] leading-relaxed break-words ${
                                isAi
                                  ? 'rounded-2xl rounded-bl-md text-amber-100 border border-amber-500/20'
                                  : isMe
                                  ? `${isSameSender ? 'rounded-2xl rounded-br-md' : 'rounded-2xl rounded-br-md'} text-white shadow-lg`
                                  : `${isSameSender ? 'rounded-2xl rounded-bl-md' : 'rounded-2xl rounded-bl-md'} text-gray-200 border border-white/[0.08]`
                              }`} style={isAi ? { background: 'rgba(245,158,11,0.08)' } : isMe ? { background: 'var(--theme-primary)', boxShadow: '0 2px 12px rgba(var(--theme-primary-rgb), 0.25)' } : { background: 'rgba(255,255,255,0.06)', backdropFilter: 'blur(8px)' }}>
                                {msg.content}
                                <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : ''}`}>
                                  <span className={`text-[10px] ${isAi ? 'text-amber-300/50' : isMe ? 'text-white/60' : 'text-gray-600'} opacity-60 group-hover:opacity-100 transition-opacity`}>
                                    {msg.created_at ? formatDate(new Date(msg.created_at), { hour: '2-digit', minute: '2-digit' }) : ''}
                                  </span>
                                  {isMe && <CheckCheck className="w-3.5 h-3.5 text-white/50" />}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                  <div ref={endRef} />
                </div>

                {/* Input area - MSN style */}
                <div className="px-4 sm:px-5 py-3 border-t shrink-0" style={{background: 'rgba(14,24,38,0.95)', borderColor: 'rgba(59,130,246,0.15)'}}>
                  {/* Quick Replies bar - only for client conversations */}
                  {selectedConvo?._type === 'client' && showQuickReplies && (
                    <div className="mb-2 p-2 rounded-xl bg-white/[0.03] border border-white/5" data-testid="quick-replies-panel">
                      <div className="flex items-center justify-between mb-2 px-1">
                        <span className="text-[10px] font-bold text-gray-500 uppercase tracking-wider">{t('staffChat.quickRepliesTitle')}</span>
                        <button onClick={() => setShowQuickReplies(false)} className="text-gray-600 hover:text-white transition-colors"><X className="w-3.5 h-3.5" /></button>
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {quickReplies.map((qr, i) => (
                          <button key={qr.id || i} onClick={() => sendQuickReply(qr.text)}
                            className={`px-3 py-1.5 rounded-lg text-[11px] font-semibold border transition-all ${QR_COLORS[qr.color] || QR_COLORS.blue}`}
                            title={qr.text}
                            data-testid={`quick-reply-${i}`}>
                            {qr.label}
                          </button>
                        ))}
                      </div>
                    </div>
                  )}
                  {/* Quick emoji bar */}
                  {showEmojis && (
                    <div className="flex items-center gap-1 mb-2 px-1 py-1 rounded-lg bg-white/5 overflow-x-auto" data-testid="emoji-bar">
                      {QUICK_EMOJIS.map((e, i) => (
                        <button key={i} onClick={() => { setMessage(prev => prev + e); setShowEmojis(false); }}
                          className="w-8 h-8 rounded-lg hover:bg-white/10 flex items-center justify-center text-lg transition-colors shrink-0">
                          {e}
                        </button>
                      ))}
                    </div>
                  )}
                  {sending && (
                    <div className="flex items-center gap-2 mb-2 px-2">
                      <div className="flex gap-1">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{animationDelay: '0ms'}} />
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{animationDelay: '150ms'}} />
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-400 animate-bounce" style={{animationDelay: '300ms'}} />
                      </div>
                      <span className="text-[10px] text-blue-300/60">{t('staffChat.sending')}</span>
                    </div>
                  )}
                  <div className="flex items-end gap-2">
                    <button onClick={() => setShowEmojis(!showEmojis)}
                      className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all shrink-0 ${showEmojis ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-gray-500 hover:text-amber-400 hover:bg-white/10'}`}
                      data-testid="emoji-toggle">
                      <Smile className="w-5 h-5" />
                    </button>
                    {selectedConvo?._type === 'client' && (
                      <button onClick={() => { setShowQuickReplies(!showQuickReplies); setShowEmojis(false); }}
                        className={`w-10 h-10 rounded-xl flex items-center justify-center transition-all shrink-0 ${showQuickReplies ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/5 text-gray-500 hover:text-cyan-400 hover:bg-white/10'}`}
                        title={t('staffChat.quickRepliesTitle')}
                        data-testid="quick-reply-toggle">
                        <Bolt className="w-5 h-5" />
                      </button>
                    )}
                    <div className="flex-1 relative">
                      <textarea
                        value={message}
                        onChange={(e) => { setMessage(e.target.value); e.target.style.height = 'auto'; e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px'; }}
                        onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); sendMessage(); } }}
                        placeholder={t('staffChat.messagePlaceholder')}
                        rows={1}
                        className="w-full px-4 py-3 rounded-2xl text-sm bg-white/[0.06] border text-white placeholder-blue-200/30 focus:border-blue-400/40 focus:bg-white/[0.08] outline-none transition-all resize-none"
                        style={{maxHeight: '120px', borderColor: 'rgba(59,130,246,0.2)'}}
                        data-testid="chat-input"
                      />
                    </div>
                    <button onClick={sendMessage} disabled={sending || !message.trim()}
                      className="w-11 h-11 rounded-2xl text-white flex items-center justify-center transition-all disabled:opacity-30 disabled:cursor-not-allowed shadow-lg shrink-0 hover:scale-105 active:scale-95"
                      style={{ background: 'linear-gradient(135deg, #3b82f6, #0891b2)' }}
                      data-testid="chat-send-btn">
                      <Send className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </>
            ) : (
              <div className="flex-1 flex items-center justify-center text-gray-600">
                <div className="text-center">
                  <div className="w-24 h-24 rounded-full bg-white/[0.02] flex items-center justify-center mx-auto mb-5">
                    <MessageCircle className="w-12 h-12 opacity-10" />
                  </div>
                  <p className="text-sm text-gray-500">{t('staffChat.selectConversation')}</p>
                  <p className="text-xs text-gray-700 mt-1">{t('staffChat.chooseFromList')}</p>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ChatPage;
